import * as React from "react";

import { ArrowedLI, UL } from "kanopi-components/List";
import {
  HeadingLarge,
  HeadingMedium,
  HeadingSmall,
  LabelSmall,
  ParagraphMedium,
} from "kanopi-components/typography";

import { BackButton } from "kw/util/Question/inputs/BackButton";
import { Block } from "baseui/block";
import { Card } from "kanopi-components/Card";
import { ChatFlow } from "kw/util/ChatFlow";
import { ConfirmationCircle } from "kw/util/ConfirmationCircle";
import { Currency } from "kanopi-components/Currency";
import { HighlightRectangle } from "kanopi-components/HighlightRectangle/HighlightRectangle";
import { Kontainer } from "kanopi-components/Layout/Kontainer";
import { Link } from "kanopi-components/Link";
import { NavButton } from "kw/components/NavButton/NavButton";
import { Notification } from "baseui/notification";
import { Placeholder } from "kanopi-components/Placeholder";
import { PolicyDocumentPollList } from "kw/util/DocumentPollList/DocumentPollList";
import { PolicyFixedFooter } from "../components/PolicyFixedFooter";
import { PriceAvailable } from "../../quoting/components/QuotePrice";
import { QuoteState } from "kw/api/zeus";
import { Referral } from "kw/components/QuestionFlow/Referral";
import { SidePadder } from "kanopi-components/Layout/SidePadder";
import { WidthContainer } from "kw/util/ChatFlow/WidthContainer";
import { frequencyToString } from "kw/util/utils";
import { navigate } from "@reach/router";
import { useConfirmPolicyUpdate } from "../hooks/useUpdatePolicy";
import { useGlobal } from "reactn";
import { usePolicy } from "../hooks/usePolicy";
import { usePolicyUpdateSummary } from "../hooks/useUpdatePolicy";
import { useStyletron } from "kanopi-components/theme/useStyletron";

enum UpdateState {
  Confirm,
  Confirmed,
}

type TConfirmUpdatePolicyPage = {
  policyId: string;
  location: {
    state: {
      startDate: string;
    };
  };
};

export function ConfirmUpdatePolicyPage({
  policyId,
  location,
}: TConfirmUpdatePolicyPage) {
  const [updateState, setUpdateState] = React.useState(UpdateState.Confirm);
  const [policy, { policyLoading }] = usePolicy(policyId);
  // Endorsement policy start(effective) date should be greater than or equal to the nb policy start date
  const nbPolicyStartDate = policy?.startDate;
  const currentOrStateStartDate =
    location.state && location.state.startDate
      ? location.state.startDate
      : new Date().toISOString().split("T")[0];
  const startDate = nbPolicyStartDate
    ? nbPolicyStartDate > currentOrStateStartDate
      ? nbPolicyStartDate
      : currentOrStateStartDate
    : currentOrStateStartDate;
  const isReferred =
    policyLoading === false &&
    policy?.latestUpdate?.quote?.state === QuoteState.REFERRED;
  const skipPayments = policy?.quote?.product?.skipPayments || false;

  return (
    <Placeholder loading={policyLoading}>
      {isReferred ? (
        <Referral riskProfile={policy?.latestUpdate?.quote?.riskProfile} />
      ) : (
        <>
          {updateState === UpdateState.Confirm && (
            <ConfirmUpdatePolicy
              policyId={policyId}
              startDate={startDate}
              setUpdateState={setUpdateState}
              skipPayments={skipPayments}
            />
          )}
          {updateState === UpdateState.Confirmed && (
            <ConfirmedUpdatePolicy policyId={policyId} />
          )}
        </>
      )}
    </Placeholder>
  );
}

type TConfirmUpdatePolicy = {
  policyId: string;
  startDate: string;
  setUpdateState: (...args: unknown[]) => void;
  skipPayments: boolean;
};

export function ConfirmUpdatePolicy({
  policyId,
  startDate,
  setUpdateState,
  skipPayments,
}: TConfirmUpdatePolicy) {
  const { updateSummary, loading } = usePolicyUpdateSummary({ policyId });
  const [errorMessage, setErrorMessage] = React.useState("");
  const [, theme] = useStyletron();
  const { confirmPolicyUpdate, loading: confirmingPolicyUpdate } =
    useConfirmPolicyUpdate({
      policyId,
      startDate,
    });
  const isRefund = updateSummary?.proRataAmount < 0;
  const [autoRenew = {}] = useGlobal(`autoRenew-update-${policyId}`);

  const handleConfirmUpdate = async () => {
    try {
      await confirmPolicyUpdate(autoRenew?.enabled);
      setUpdateState(UpdateState.Confirmed);
    } catch (err) {
      console.error(err);
      setErrorMessage("An unexpected error occurred when updating your Policy.");
    }
  };

  const handleBackClick = () => {
    navigate(`/policies/${policyId}/update/knockout`);
  };

  return (
    <Placeholder loading={loading}>
      <Block minHeight={theme.sizing.scale950}>
        <BackButton onClick={handleBackClick} />
      </Block>
      <ChatFlow title="Confirm your updates" loading={loading}>
        <SidePadder>
          <HighlightRectangle>
            <LabelSmall>
              Your{" "}
              {updateSummary?.newPeriodicPrice === updateSummary?.oldPeriodicPrice
                ? ""
                : "new"}{" "}
              premium
            </LabelSmall>
            <PriceAvailable
              periodicPrice={updateSummary?.newPeriodicPrice}
              paymentFrequency={updateSummary?.newPaymentFrequency}
            />
          </HighlightRectangle>
          <Block display="flex" flexDirection="column" alignItems="center">
            <WidthContainer>
              {(updateSummary?.summary.length > 0 || !skipPayments) && (
                <HeadingLarge center>Confirm your policy update</HeadingLarge>
              )}

              {!skipPayments && (
                <>
                  {updateSummary?.newPeriodicPrice ===
                    updateSummary?.oldPeriodicPrice && (
                    <ParagraphMedium center>
                      Your premium has not changed.
                    </ParagraphMedium>
                  )}

                  {updateSummary?.newPeriodicPrice !==
                    updateSummary?.oldPeriodicPrice && (
                    <Kontainer>
                      {!isRefund ? (
                        <ParagraphMedium center>
                          Your new premium will be{" "}
                          <Currency value={updateSummary?.newPeriodicPrice} />{" "}
                          {frequencyToString(
                            updateSummary?.newPaymentFrequency
                          ).toLocaleLowerCase()}
                          , starting from your next payment on{" "}
                          {new Date(updateSummary?.newStartDate).toLocaleDateString()}.
                        </ParagraphMedium>
                      ) : null}
                      <ParagraphMedium center>
                        We will {isRefund ? "refund" : "charge"} a total amount of{" "}
                        <Currency value={Math.abs(updateSummary?.amountDue)} /> (pro
                        rata <Currency value={Math.abs(updateSummary?.proRataAmount)} />{" "}
                        + surcharge{" "}
                        <Currency value={Math.abs(updateSummary?.surcharge)} />) to your
                        {isRefund ? (
                          " original card"
                        ) : (
                          <Link onClick={() => navigate("/account")}>
                            {" "}
                            default card
                          </Link>
                        )}{" "}
                        today. This covers the difference between your old and new
                        premium for the remaining days in this payment period.
                      </ParagraphMedium>
                    </Kontainer>
                  )}
                </>
              )}

              {updateSummary?.summary.length > 0 && (
                <Kontainer>
                  <HeadingMedium center>Changes you made to your policy</HeadingMedium>
                  <UL marginBottom="scale1200">
                    {updateSummary?.summary.map((item, i) => (
                      <ArrowedLI key={i}>{item}</ArrowedLI>
                    ))}
                  </UL>
                </Kontainer>
              )}

              {errorMessage && (
                <Notification
                  kind="negative"
                  overrides={{ Body: { style: { width: "auto" } } }}
                >
                  {errorMessage}
                </Notification>
              )}
            </WidthContainer>
          </Block>
        </SidePadder>
      </ChatFlow>
      <PolicyFixedFooter>
        <NavButton
          disabled={confirmingPolicyUpdate || !!errorMessage}
          isLoading={confirmingPolicyUpdate}
          onClick={handleConfirmUpdate}
          data-testid="confirmPolicyUpdateButton"
        >
          Confirm changes
        </NavButton>
      </PolicyFixedFooter>
    </Placeholder>
  );
}

type TConfirmedUpdatePolicy = {
  policyId: string;
};

export function ConfirmedUpdatePolicy({ policyId }: TConfirmedUpdatePolicy) {
  const [policy, { policyLoading }] = usePolicy(policyId);
  const handleDone = () => {
    navigate(`/policies/${policyId}`);
  };
  const [, theme] = useStyletron();
  return (
    <Placeholder loading={policyLoading}>
      <Kontainer>
        <Block
          display="flex"
          flexDirection="column"
          alignItems="center"
          paddingLeft="scale600"
          paddingRight="scale600"
          paddingBottom="scale1200"
        >
          <WidthContainer>
            <ConfirmationCircle />
            <HeadingSmall marginTop="scale1200" marginBottom="0">
              Update successful
            </HeadingSmall>
            <HeadingLarge marginTop="scale400" center>
              Your policy has been updated
            </HeadingLarge>
            <Card
              display="flex"
              flexDirection="column"
              alignItems="center"
              width="90%"
              backgroundColor={theme.colors.backgroundTertiary}
              borderRadius="5px"
              paddingLeft="scale100"
              paddingRight="scale100"
              paddingTop="scale600"
              paddingBottom="scale600"
            >
              <HeadingSmall marginTop="0" marginBottom="scale100">
                Your policy number:
              </HeadingSmall>
              <HeadingSmall marginTop="0" marginBottom="0">
                {policy?.policyNumber}
              </HeadingSmall>
            </Card>
            <ParagraphMedium marginTop="scale1200" center>
              We’ve sent you an email with your updated policy details.
            </ParagraphMedium>
            <PolicyDocumentPollList riskProfileId={policy?.riskProfile?.id} />
          </WidthContainer>
        </Block>
      </Kontainer>
      <PolicyFixedFooter>
        <NavButton onClick={handleDone}>Done</NavButton>
      </PolicyFixedFooter>
    </Placeholder>
  );
}
